// src/feature-module/crm/power-dialer/PowerDialerCampaigns.jsx
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../../core/axios/axiosInstance";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import {
  setPowerDialerSession,
  setPowerDialerPaused,
  setPowerDialerResumed,
  clearPowerDialerSession,
  setPowerDialerNextNumber,
} from "../../../core/data/redux/slices/PowerDialerSlice";
import TableSkeleton from "../../../core/common/TableSkeleton";

let _allCache = { lists: null, campaigns: null };
let _agentsCache = { data: null };

const CARD_SHADOW = "0 1px 3px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.05)";

const DISPOSITION_OPTIONS = [
  { value: "callBack", label: "Call Back" },
  { value: "noAnswer", label: "No Answer" },
  { value: "interested", label: "Meeting" },
  { value: "callSuccess", label: "Call Success" },
];

function StatCard({ label, value, icon, iconBg, iconColor, skeleton }) {
  return (
    <div className="col-6 col-md-3">
      <div className="card border-0 h-100" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
        <div className="card-body py-3 px-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p
                className="mb-1 fw-semibold text-muted text-uppercase"
                style={{ fontSize: 11, letterSpacing: ".6px" }}
              >
                {label}
              </p>
              <h3 className="fw-bold mb-0" style={{ fontSize: 26 }}>
                {skeleton
                  ? <div className="contact-skel" style={{ width: 48, height: 28, borderRadius: 6 }} />
                  : value}
              </h3>
            </div>
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{ width: 46, height: 46, background: iconBg }}
            >
              <i className={`ti ${icon} fs-4`} style={{ color: iconColor }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusMeta = (status) => {
  const map = {
    active: { bg: "rgba(16,185,129,.14)", color: "#065f46", label: "Active" },
    paused: { bg: "rgba(245,158,11,.14)", color: "#92400e", label: "Paused" },
    completed: { bg: "rgba(59,130,246,.14)", color: "#1d4ed8", label: "Completed" },
    stopped: { bg: "rgba(107,114,128,.12)", color: "#374151", label: "Stopped" },
  };
  return map[status] || { bg: "#f3f4f6", color: "#6b7280", label: status };
};

function InlineBadge({ bg, color, label }) {
  return (
    <span
      className="fw-semibold"
      style={{ background: bg, color, borderRadius: 6, padding: "3px 10px", fontSize: 12 }}
    >
      {label}
    </span>
  );
}

function SegmentedTabs({ tabs, activeTab, onChange }) {
  return (
    <div
      className="d-inline-flex p-1 gap-1 mb-4"
      style={{ background: "#f0f2f5", borderRadius: 12 }}
    >
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className="btn btn-sm d-flex align-items-center gap-2 fw-semibold"
            style={{
              borderRadius: 9,
              border: "none",
              padding: "7px 18px",
              fontSize: 13,
              background: active ? "#fff" : "transparent",
              color: active ? "#111827" : "#6b7280",
              boxShadow: active ? "0 1px 4px rgba(0,0,0,.1)" : "none",
              transition: "all .15s",
            }}
          >
            {tab.icon && <i className={`ti ${tab.icon}`} style={{ fontSize: 15 }} />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}


function ConfirmModal({ show, title, message, confirmLabel = "Confirm", confirmColor = "#dc2626", confirmBg = "rgba(239,68,68,.12)", onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
          <div className="modal-body px-4 pt-4 pb-3">
            <h6 className="fw-bold mb-2" style={{ color: "#111827", fontSize: 16 }}>{title}</h6>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>{message}</p>
          </div>
          <div className="modal-footer border-0 px-4 pt-0 pb-4 gap-2">
            <button
              className="btn fw-semibold px-4"
              style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="btn fw-semibold px-4"
              style={{ background: confirmBg, color: confirmColor, borderRadius: 10, border: "none" }}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="form-label fw-semibold mb-2" style={{ fontSize: 13, color: "#374151" }}>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function PowerDialerCampaigns() {
  const dispatch = useDispatch();
  const userProfile = useSelector((s) => s.profile);
  const powerDialer = useSelector((s) => s.powerDialer);
  const isAdmin = userProfile?.role === "companyAdmin";

  const [activeTab, setActiveTab] = useState(isAdmin ? "create" : "list");
  const [lists, setLists] = useState(_allCache.lists || []);
  const [campaigns, setCampaigns] = useState(_allCache.campaigns || []);
  const [isInitialLoad, setIsInitialLoad] = useState(!_allCache.campaigns);
  const [refreshing, setRefreshing] = useState(false);
  const [agents, setAgents] = useState([]);
  const [liveData, setLiveData] = useState({ stats: {}, rows: [] });
  const liveIntervalRef = useRef(null);
  const [myNumbers, setMyNumbers] = useState([]);

  const getCallerLabel = (number) => {
    const didLabel = (userProfile?.didLabels || []).find((d) => d.number === number);
    if (didLabel?.nickname) return `${didLabel.nickname} (+${number})`;
    const extNickname = (userProfile?.assignedExtensions || []).find(
      (e) => e.PBX_TELEPHONE === number
    )?.nickname;
    if (extNickname) return `${extNickname} (+${number})`;
    return `+${number}`;
  };

  const [form, setForm] = useState({
    list_id: "",
    name: "",
    description: "",
    assignees: [],
    from_number: "",
    from_number_type: "number",
    call_dispositions: [],
  });
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [confirm, setConfirm] = useState({ show: false, title: "", message: "", confirmLabel: "Confirm", confirmColor: "#dc2626", confirmBg: "rgba(239,68,68,.12)", onConfirm: null });

  // ── Edit campaign ────────────────────────────────────────────────────────────
  const [editModal, setEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  // ── View campaign contacts ────────────────────────────────────────────────────
  const [viewModal, setViewModal] = useState(false);
  const [viewCampaign, setViewCampaign] = useState(null);
  const [viewContacts, setViewContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [campaignPage, setCampaignPage] = useState(1);
  const CAMPAIGN_PAGE_SIZE = 10;

  const handleViewContacts = async (campaign) => {
    const listId = campaign.list_id?._id || campaign.list_id;
    if (!listId) {
      dispatch(showToast({ message: "Contact list not found. It may have been deleted from the Lists section.", variant: "warning" }));
      return;
    }
    setViewCampaign(campaign);
    setViewContacts([]);
    setViewModal(true);
    setLoadingContacts(true);
    try {
      const campaignId = campaign._id || campaign.id;
      const res = await api.get(`/api/power-dialer/lists/${listId}/contacts?campaign_id=${campaignId}`);
      setViewContacts(res.data.data || []);
    } catch {
      dispatch(showToast({ message: "Failed to load contacts", variant: "danger" }));
    } finally {
      setLoadingContacts(false);
    }
  };

  const DISPOSITION_LABELS = {
    callBack: "Call Back",
    noAnswer: "No Answer",
    interested: "Meeting",
    callSuccess: "Call Success",
  };

  const fetchCampaignContacts = async (campaign) => {
    const listId = campaign.list_id?._id || campaign.list_id;
    if (!listId) return [];
    try {
      const res = await api.get(`/api/power-dialer/lists/${listId}/contacts`);
      return res.data.data || [];
    } catch {
      return [];
    }
  };

  const handleDownloadExcel = async (campaign) => {
    const contacts = await fetchCampaignContacts(campaign);

    const calledFrom = campaign.from_number ? `+${campaign.from_number}` : "—";
    const total   = contacts.length;
    const called  = contacts.filter((ct) => ct.status && ct.status !== "pending").length;
    const pending = contacts.filter((ct) => !ct.status || ct.status === "pending").length;

    /* ── Single sheet: everything together ── */
    const aoa = [
      ["CAMPAIGN REPORT"],
      ["Generated:", new Date().toLocaleString()],
      [],
      ["── CAMPAIGN DETAILS ──"],
      ["Campaign Name",   campaign.name],
      ["Description",     campaign.description || "—"],
      ["Contact List",    campaign.list_id?.name || "—"],
      ["Called From",     calledFrom],
      ["Dispositions",    (campaign.call_dispositions || []).map((d) => DISPOSITION_LABELS[d] || d).join(", ") || "—"],
      ["Campaign Status", campaign.status],
      ["Created By",      campaign.created_by ? `${campaign.created_by.firstname} ${campaign.created_by.lastname}` : "—"],
      ["Created Date",    new Date(campaign.created_at).toLocaleDateString()],
      [],
      ["── CALL STATISTICS ──"],
      ["Total Contacts",   total],
      ["Called",           called],
      ["Pending",          pending],
      ["Calls Completed",  campaign.calls_completed || 0],
      ["Call Success",     contacts.filter((ct) => ct.disposition === "callSuccess").length],
      ["Meeting",          contacts.filter((ct) => ct.disposition === "interested").length],
      ["Call Back",        contacts.filter((ct) => ct.disposition === "callBack").length],
      ["No Answer",        contacts.filter((ct) => ct.disposition === "noAnswer").length],
      [],
      ["── CONTACTS ──"],
      ["#", "Name", "Phone", "Called From", "Call Status", "Disposition", "Notes"],
      ...contacts.map((ct, i) => [
        i + 1,
        ct.name || "—",
        ct.phone || "—",
        calledFrom,
        ct.status || "pending",
        ct.disposition ? (DISPOSITION_LABELS[ct.disposition] || ct.disposition) : "—",
        ct.notes || "",
      ]),
      ...(contacts.length === 0 ? [["", "No contacts found", "", "", "", "", ""]] : []),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 18 }, { wch: 24 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaign Report");

    XLSX.writeFile(wb, `campaign_${campaign.name.replace(/\s+/g, "_")}.xlsx`);
  };

  const handleDownloadPDF = async (campaign) => {
    const contacts = await fetchCampaignContacts(campaign);

    const calledFrom  = campaign.from_number ? `+${campaign.from_number}` : "—";
    const total       = contacts.length;
    const called      = contacts.filter((ct) => ct.status && ct.status !== "pending").length;
    const pending     = contacts.filter((ct) => !ct.status || ct.status === "pending").length;
    const callSuccess = contacts.filter((ct) => ct.disposition === "callSuccess").length;
    const meeting     = contacts.filter((ct) => ct.disposition === "interested").length;
    const callBack    = contacts.filter((ct) => ct.disposition === "callBack").length;
    const noAnswer    = contacts.filter((ct) => ct.disposition === "noAnswer").length;
    const createdBy   = campaign.created_by ? `${campaign.created_by.firstname} ${campaign.created_by.lastname}` : "—";

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const PW = doc.internal.pageSize.getWidth();
    const PH = doc.internal.pageSize.getHeight();

    const drawFooter = () => {
      const pages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFillColor(245, 247, 255);
        doc.rect(0, PH - 12, PW, 12, "F");
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(`${campaign.name}  •  Generated ${new Date().toLocaleString()}`, 14, PH - 4.5);
        doc.text(`Page ${p} of ${pages}`, PW - 14, PH - 4.5, { align: "right" });
      }
    };

    /* ── Header bar ── */
    doc.setFillColor(79, 70, 229);
    doc.rect(0, 0, PW, 26, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Campaign Report", 14, 11);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(campaign.name, 14, 20);
    doc.setFontSize(8.5);
    doc.setTextColor(199, 210, 254);
    doc.text(`Called From: ${calledFrom}`, PW - 14, 20, { align: "right" });

    /* ── Campaign info + Stats side by side ── */
    const half = (PW - 32) / 2;

    autoTable(doc, {
      startY: 32,
      margin: { left: 14, right: PW / 2 + 2 },
      tableWidth: half,
      head: [["Campaign Details", ""]],
      body: [
        ["Campaign",     campaign.name],
        ["List",         campaign.list_id?.name || "—"],
        ["Called From",  calledFrom],
        ["Status",       campaign.status],
        ["Created By",   createdBy],
        ["Date",         new Date(campaign.created_at).toLocaleDateString()],
        ["Dispositions", (campaign.call_dispositions || []).map((d) => DISPOSITION_LABELS[d] || d).join(", ") || "—"],
        ...(campaign.description ? [["Notes", campaign.description]] : []),
      ],
      headStyles:         { fillColor: [79, 70, 229], fontSize: 8.5, fontStyle: "bold" },
      bodyStyles:         { fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles:       { 0: { fontStyle: "bold", cellWidth: 24, textColor: [79, 70, 229] } },
      theme: "grid",
    });
    const leftY = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: 32,
      margin: { left: PW / 2 + 2, right: 14 },
      tableWidth: half,
      head: [["Call Statistics", ""]],
      body: [
        ["Total Contacts",  String(total)],
        ["Called",          String(called)],
        ["Pending",         String(pending)],
        ["Calls Completed", String(campaign.calls_completed || 0)],
        ["Call Success",    String(callSuccess)],
        ["Meeting",         String(meeting)],
        ["Call Back",       String(callBack)],
        ["No Answer",       String(noAnswer)],
      ],
      headStyles:         { fillColor: [16, 185, 129], fontSize: 8.5, fontStyle: "bold" },
      bodyStyles:         { fontSize: 8 },
      alternateRowStyles: { fillColor: [240, 253, 249] },
      columnStyles:       { 0: { fontStyle: "bold", cellWidth: 32, textColor: [6, 95, 70] } },
      theme: "grid",
    });
    const rightY = doc.lastAutoTable.finalY;

    /* ── Contacts table ── */
    const contactsY = Math.max(leftY, rightY) + 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(31, 41, 55);
    doc.text(`Contacts  (${total})`, 14, contactsY);

    autoTable(doc, {
      startY: contactsY + 4,
      margin: { left: 14, right: 14, bottom: 16 },
      head: [["#", "Name", "Phone", "Called From", "Status", "Disposition", "Notes"]],
      body: contacts.length
        ? contacts.map((ct, i) => [
            i + 1,
            ct.name || "—",
            ct.phone || "—",
            calledFrom,
            ct.status || "pending",
            ct.disposition ? (DISPOSITION_LABELS[ct.disposition] || ct.disposition) : "—",
            ct.notes || "",
          ])
        : [["", "No contacts found", "", "", "", "", ""]],
      headStyles:         { fillColor: [30, 64, 175], fontSize: 8.5, fontStyle: "bold", halign: "center" },
      bodyStyles:         { fontSize: 8, halign: "center" },
      alternateRowStyles: { fillColor: [248, 250, 255] },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 35, halign: "left" },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 },
        6: { halign: "left" },
      },
      theme: "grid",
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 4) {
          const v = String(data.cell.raw || "").toLowerCase();
          if (v === "called")    { data.cell.styles.textColor = [6, 95, 70];    data.cell.styles.fontStyle = "bold"; }
          if (v === "no_answer") { data.cell.styles.textColor = [146, 64, 14];  data.cell.styles.fontStyle = "bold"; }
          if (v === "busy")      { data.cell.styles.textColor = [55, 48, 163];  data.cell.styles.fontStyle = "bold"; }
          if (v === "failed")    { data.cell.styles.textColor = [153, 27, 27];  data.cell.styles.fontStyle = "bold"; }
          if (v === "pending")   { data.cell.styles.textColor = [107, 114, 128]; }
        }
        if (data.column.index === 5) {
          const v = String(data.cell.raw || "");
          if (v === "Call Success") { data.cell.styles.textColor = [6, 95, 70];   data.cell.styles.fontStyle = "bold"; }
          if (v === "Meeting")      { data.cell.styles.textColor = [29, 78, 216]; data.cell.styles.fontStyle = "bold"; }
          if (v === "Call Back")    { data.cell.styles.textColor = [146, 64, 14]; data.cell.styles.fontStyle = "bold"; }
          if (v === "No Answer")    { data.cell.styles.textColor = [107, 114, 128]; }
        }
      },
    });

    drawFooter();
    doc.save(`campaign_${campaign.name.replace(/\s+/g, "_")}.pdf`);
  };

  const openConfirm = (title, message, onConfirm, opts = {}) =>
    setConfirm({ show: true, title, message, onConfirm, confirmLabel: opts.confirmLabel || "Confirm", confirmColor: opts.confirmColor || "#dc2626", confirmBg: opts.confirmBg || "rgba(239,68,68,.12)" });
  const closeConfirm = () => setConfirm((p) => ({ ...p, show: false }));

  const fetchMyNumbers = async () => {
    try {
      const res = await api.get("/didlogic/numbers/calling-numbers");
      const purchased = (res.data.data || []).map((n) => n.number);

      // PBX extensions assigned to this admin
      const extensions = (userProfile?.assignedExtensions || []).map(
        (e) => e.PBX_TELEPHONE
      );

      // Fallback single telephone
      const tel = userProfile?.telephone;

      const allNumbers = [
        ...new Set([...purchased, ...extensions, ...(tel ? [tel] : [])]),
      ].filter(Boolean);

      setMyNumbers(
        allNumbers.map((num) => ({
          value: num,
          label: getCallerLabel(num),
        }))
      );
    } catch {}
  };

  const fetchAll = async (forceRefresh = false) => {
    if (!forceRefresh && _allCache.campaigns) {
      setLists(_allCache.lists || []);
      setCampaigns(_allCache.campaigns);
      setIsInitialLoad(false);
      return;
    }
    setIsInitialLoad(true);
    setRefreshing(forceRefresh);
    try {
      const [listsRes, campaignsRes] = await Promise.all([
        api.get("/api/power-dialer/lists"),
        api.get("/api/power-dialer/campaigns"),
      ]);
      const listsData = listsRes.data.data || [];
      const campaignsData = campaignsRes.data.data || [];
      setLists(listsData);
      setCampaigns(campaignsData);
      _allCache = { lists: listsData, campaigns: campaignsData };
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.message || "Failed to load campaigns", variant: "danger" }));
    } finally {
      setIsInitialLoad(false);
      setRefreshing(false);
    }
  };

  const fetchAgents = async () => {
    if (_agentsCache.data) {
      setAgents(_agentsCache.data);
      return;
    }
    try {
      const res = await api.get("/getUser/getAgents");
      const data = (res.data.users || []).map((u) => ({
        value: u._id,
        label: `${u.firstname} ${u.lastname}`,
      }));
      setAgents(data);
      _agentsCache = { data };
    } catch {}
  };

  const fetchLive = async () => {
    try {
      const res = await api.get("/api/power-dialer/live");
      setLiveData(res.data.data || { stats: {}, rows: [] });
    } catch {}
  };

  useEffect(() => {
    fetchAll();
    fetchAgents();
  }, []);

  useEffect(() => {
    if (powerDialer.campaignCompletedAt) fetchAll(true);
  }, [powerDialer.campaignCompletedAt]);

  useEffect(() => {
    if (!openDropdownId) return;
    const close = () => setOpenDropdownId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openDropdownId]);

  // Re-build number list whenever profile loads (extensions/labels live in profile)
  useEffect(() => {
    if (userProfile) fetchMyNumbers();
  }, [userProfile?.assignedExtensions, userProfile?.didLabels, userProfile?.telephone]);

  useEffect(() => {
    if (activeTab === "live") {
      fetchLive();
      liveIntervalRef.current = setInterval(fetchLive, 5000);
    }
    return () => clearInterval(liveIntervalRef.current);
  }, [activeTab]);

  const handleCreateCampaign = async () => {
    if (!form.list_id || !form.name.trim()) {
      dispatch(showToast({ message: "List and Campaign Name required", variant: "danger" }));
      return;
    }
    setSavingCampaign(true);
    try {
      const payload = {
        ...form,
        assignees: form.assignees.map((a) => a.value),
        call_dispositions: form.call_dispositions.map((d) => d.value),
      };
      await api.post("/api/power-dialer/campaigns", payload);
      dispatch(showToast({ message: "Campaign created", variant: "success" }));
      setForm({
        list_id: "", name: "", description: "",
        assignees: [], from_number: "", from_number_type: "number",
        call_dispositions: [],
      });
      fetchAll(true);
      setActiveTab("list");
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed", variant: "danger" }));
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleDeleteCampaign = (id) =>
    openConfirm(
      "Delete Campaign",
      "This will permanently delete the campaign. This cannot be undone.",
      async () => {
        closeConfirm();
        try {
          await api.delete(`/api/power-dialer/campaigns/${id}`);
          dispatch(showToast({ message: "Campaign deleted", variant: "success" }));
          fetchAll(true);
        } catch {
          dispatch(showToast({ message: "Delete failed", variant: "danger" }));
        }
      }
    );

  const handleOpenEdit = (campaign) => {
    setEditTarget(campaign);
    setEditForm({
      name: campaign.name || "",
      description: campaign.description || "",
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      dispatch(showToast({ message: "Campaign name required", variant: "danger" }));
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/api/power-dialer/campaigns/${editTarget._id}`, {
        name: editForm.name.trim(),
        description: editForm.description,
      });
      dispatch(showToast({ message: "Campaign updated", variant: "success" }));
      setEditModal(false);
      fetchAll(true);
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Update failed", variant: "danger" }));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === "active" ? "paused" : "active";
    try {
      await api.put(`/api/power-dialer/campaigns/${campaign._id}/status`, { status: newStatus });
      fetchAll(true);
    } catch {
      dispatch(showToast({ message: "Status update failed", variant: "danger" }));
    }
  };

  const handleStartSession = async (campaignId) => {
    try {
      const res = await api.post("/api/power-dialer/sessions", { campaign_id: campaignId });
      const { session, contact, from_number } = res.data.data;
      dispatch(setPowerDialerSession({ session, contact, fromNumber: from_number || null }));
      dispatch(showToast({ message: "Power Dialer started!", variant: "success" }));
      if (contact?.phone) {
        setTimeout(() => dispatch(setPowerDialerNextNumber(contact.phone)), 500);
      }
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to start", variant: "danger" }));
      fetchAll(true);
    }
  };

  const handlePauseSession = async () => {
    if (!powerDialer.session) return;
    try {
      await api.put(`/api/power-dialer/sessions/${powerDialer.session._id}/pause`);
      dispatch(setPowerDialerPaused());
    } catch {
      dispatch(showToast({ message: "Pause failed", variant: "danger" }));
    }
  };

  const handleResumeSession = async () => {
    if (!powerDialer.session) return;
    try {
      const res = await api.put(`/api/power-dialer/sessions/${powerDialer.session._id}/resume`);
      dispatch(setPowerDialerResumed());
      const { contact } = res.data.data;
      if (contact?.phone) dispatch(setPowerDialerNextNumber(contact.phone));
    } catch {
      dispatch(showToast({ message: "Resume failed", variant: "danger" }));
    }
  };

  const handleStopSession = () => {
    if (!powerDialer.session) return;
    openConfirm(
      "Stop Session",
      "This will stop the current Power Dialer session. You can start a new one anytime.",
      async () => {
        closeConfirm();
        try {
          await api.put(`/api/power-dialer/sessions/${powerDialer.session._id}/stop`);
          dispatch(clearPowerDialerSession());
          dispatch(showToast({ message: "Session stopped", variant: "success" }));
        } catch {
          dispatch(showToast({ message: "Stop failed", variant: "danger" }));
        }
      }
    );
  };

  const listOptions = lists.map((l) => ({
    value: l._id,
    label: `${l.name} (${l.total_contacts} contacts)`,
  }));

  const tabs = [
    ...(isAdmin ? [{ id: "create", label: "Create Campaign", icon: "ti-plus" }] : []),
    { id: "list", label: "Campaigns", icon: "ti-speakerphone" },
    ...(isAdmin ? [{ id: "live", label: "Live Calls", icon: "ti-radio" }] : []),
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div>
            <h4 className="fw-bold mb-1" style={{ color: "#111827" }}>
              Power Dialer
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              Automate outgoing calls from uploaded contact lists
            </p>
          </div>
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2 fw-semibold px-3 flex-shrink-0"
            style={{ borderRadius: 10, height: 40 }}
            disabled={refreshing}
            onClick={() => fetchAll(true)}
          >
            {refreshing
              ? <span className="spinner-border spinner-border-sm" />
              : <i className="ti ti-refresh" style={{ fontSize: 16 }} />
            }
            Refresh
          </button>
        </div>

        {/* Active session banner */}
        {powerDialer.session && (
          <div
            className="d-flex align-items-center justify-content-between mb-4 px-4 py-3"
            style={{
              background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)",
              borderRadius: 14,
              boxShadow: "0 4px 20px rgba(79,70,229,.35)",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{ width: 40, height: 40, background: "rgba(255,255,255,.2)" }}
              >
                <i className="ti ti-phone-calling fs-5 text-white" />
              </div>
              <div>
                <p className="fw-bold text-white mb-0" style={{ fontSize: 14 }}>
                  Power Dialer Active
                  {powerDialer.isPaused && (
                    <span
                      className="ms-2 fw-semibold"
                      style={{
                        background: "rgba(255,255,255,.25)",
                        color: "#fff",
                        borderRadius: 6,
                        padding: "2px 10px",
                        fontSize: 12,
                      }}
                    >
                      Paused
                    </span>
                  )}
                </p>
                {powerDialer.currentContact && (
                  <p className="text-white mb-0 opacity-75" style={{ fontSize: 13 }}>
                    Calling: {powerDialer.currentContact.name || powerDialer.currentContact.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              {powerDialer.isPaused ? (
                <button
                  className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                  style={{
                    background: "rgba(255,255,255,.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,.35)",
                    borderRadius: 9,
                    padding: "7px 16px",
                  }}
                  onClick={handleResumeSession}
                >
                  <i className="ti ti-player-play" /> Resume
                </button>
              ) : (
                <button
                  className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                  style={{
                    background: "rgba(255,255,255,.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,.35)",
                    borderRadius: 9,
                    padding: "7px 16px",
                  }}
                  onClick={handlePauseSession}
                >
                  <i className="ti ti-player-pause" /> Pause
                </button>
              )}
              <button
                className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                style={{
                  background: "rgba(239,68,68,.25)",
                  color: "#fca5a5",
                  border: "1px solid rgba(239,68,68,.4)",
                  borderRadius: 9,
                  padding: "7px 16px",
                }}
                onClick={handleStopSession}
              >
                <i className="ti ti-player-stop" /> Stop
              </button>
            </div>
          </div>
        )}

        {/* Stats (only when no active session) */}
        {!powerDialer.session && (isInitialLoad || campaigns.length > 0) && (
          <div className="row g-3 mb-4">
            <StatCard
              label="Total Campaigns"
              value={campaigns.length}
              icon="ti-speakerphone"
              iconBg="rgba(99,102,241,.12)"
              iconColor="#4f46e5"
              skeleton={isInitialLoad}
            />
            <StatCard
              label="Active"
              value={campaigns.filter((c) => c.status === "active").length}
              icon="ti-player-play"
              iconBg="rgba(16,185,129,.12)"
              iconColor="#059669"
              skeleton={isInitialLoad}
            />
            <StatCard
              label="Paused"
              value={campaigns.filter((c) => c.status === "paused").length}
              icon="ti-player-pause"
              iconBg="rgba(245,158,11,.12)"
              iconColor="#d97706"
              skeleton={isInitialLoad}
            />
          </div>
        )}

        {/* Segmented tabs */}
        <SegmentedTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* ── Create Campaign ── */}
        {activeTab === "create" && isAdmin && (
          <div className="card border-0" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
            <div className="card-body px-4 py-4">
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <FormField label="Select List" required>
                    <Select
                      options={listOptions}
                      value={listOptions.find((o) => o.value === form.list_id) || null}
                      onChange={(opt) => setForm((p) => ({ ...p, list_id: opt?.value || "" }))}
                      placeholder="Select a contact list…"
                      classNamePrefix="react-select"
                    />
                  </FormField>
                </div>
                <div className="col-md-6">
                  <FormField label="Campaign Name" required>
                    <input
                      className="form-control"
                      style={{ borderRadius: 10, fontSize: 14 }}
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Q2 Lead Outreach"
                    />
                  </FormField>
                </div>
                <div className="col-md-6">
                  <FormField label="Assignees">
                    <Select
                      isMulti
                      options={agents}
                      value={form.assignees}
                      onChange={(opts) => setForm((p) => ({ ...p, assignees: opts }))}
                      placeholder="Select agents…"
                      classNamePrefix="react-select"
                    />
                  </FormField>
                </div>
                <div className="col-md-6">
                  <FormField label="From Number">
                    <Select
                      options={myNumbers}
                      value={myNumbers.find((o) => o.value === form.from_number) || null}
                      onChange={(opt) => setForm((p) => ({ ...p, from_number: opt?.value || "" }))}
                      placeholder={myNumbers.length === 0 ? "No numbers on dialer" : "Select a number…"}
                      isDisabled={myNumbers.length === 0}
                      classNamePrefix="react-select"
                    />
                  </FormField>
                </div>
                <div className="col-md-6">
                  <FormField label="Call Dispositions">
                    <Select
                      isMulti
                      options={DISPOSITION_OPTIONS}
                      value={form.call_dispositions}
                      onChange={(opts) => setForm((p) => ({ ...p, call_dispositions: opts }))}
                      placeholder="Select dispositions…"
                      classNamePrefix="react-select"
                    />
                  </FormField>
                </div>
                <div className="col-12">
                  <FormField label="Description">
                    <textarea
                      className="form-control"
                      style={{ borderRadius: 10, fontSize: 14 }}
                      rows={3}
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Optional notes about this campaign…"
                    />
                  </FormField>
                </div>
              </div>

              <button
                className="btn btn-primary fw-semibold px-5"
                style={{ borderRadius: 10, height: 42 }}
                onClick={handleCreateCampaign}
                disabled={savingCampaign}
              >
                {savingCampaign ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="ti ti-device-floppy me-2" />
                    Save Campaign
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── Campaign List ── */}
        {activeTab === "list" && (
          <div className="card border-0" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
            {isInitialLoad ? (
              <div style={{ padding: "16px 0" }}>
                <TableSkeleton
                  rows={5}
                  rowHeight={52}
                  columns={[
                    { width: 180, type: "text" },
                    { width: 120, type: "text" },
                    { width: 120, type: "text" },
                    { width: 100, type: "badge" },
                    { width: 80,  type: "text" },
                    { width: 90,  type: "text" },
                    { width: 120, type: "text" },
                    { width: 80,  type: "badge" },
                    { width: 100, type: "action" },
                  ]}
                />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="py-5 text-center">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                  style={{ width: 56, height: 56, background: "rgba(99,102,241,.1)" }}
                >
                  <i className="ti ti-speakerphone fs-2 text-primary" />
                </div>
                <p className="fw-semibold mb-1" style={{ color: "#111827" }}>
                  No campaigns yet
                </p>
                <p className="text-muted small mb-3">
                  {isAdmin ? "Create a campaign to start dialing" : "No active campaigns assigned"}
                </p>
                {isAdmin && (
                  <button
                    className="btn btn-primary btn-sm fw-semibold px-4"
                    style={{ borderRadius: 8 }}
                    onClick={() => setActiveTab("create")}
                  >
                    <i className="ti ti-plus me-1" /> Create Campaign
                  </button>
                )}
              </div>
            ) : (
              <>
              <div className="table-responsive">
                <table className="table align-middle mb-0" style={{ borderCollapse: "separate", border: "none" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Campaign", "List", "From Number", "Dispositions", "Calls Done", "Date", "Assigned To", "Status", "Actions"].map(
                        (h, i) => (
                          <th
                            key={h}
                            className={`fw-semibold text-muted text-uppercase py-3 ${i === 0 ? "ps-4" : ""} ${i === 8 ? "pe-4" : ""}`}
                            style={{
                              fontSize: 11,
                              letterSpacing: ".6px",
                              borderBottom: "1px solid #f0f1f3",
                            }}
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody style={{ borderBottom: "none" }}>
                    {campaigns.slice((campaignPage - 1) * CAMPAIGN_PAGE_SIZE, campaignPage * CAMPAIGN_PAGE_SIZE).map((c, idx) => {
                      const sm = statusMeta(c.status);
                      return (
                        <tr
                          key={c._id}
                          style={{
                            borderBottom: idx < campaigns.length - 1 ? "1px solid #f4f5f7" : "none",
                          }}
                        >
                          <td className="ps-4 py-3">
                            <span className="fw-semibold" style={{ color: "#111827", fontSize: 14 }}>
                              {c.name}
                            </span>
                            {c.description && (
                              <p
                                className="text-muted mb-0 text-truncate"
                                style={{ fontSize: 12, maxWidth: 200 }}
                              >
                                {c.description}
                              </p>
                            )}
                          </td>
                          <td style={{ color: "#6b7280", fontSize: 13 }}>
                            {c.list_id?.name || "—"}
                          </td>
                          <td style={{ fontSize: 13 }}>
                            {c.from_number ? (
                              <span
                                className="fw-semibold"
                                style={{ background: "rgba(99,102,241,.1)", color: "#4f46e5", borderRadius: 6, padding: "3px 10px" }}
                              >
                                +{c.from_number}
                              </span>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {c.call_dispositions && c.call_dispositions.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {c.call_dispositions.map((d) => {
                                  const dispMeta = {
                                    callSuccess: { bg: "rgba(16,185,129,.14)", color: "#065f46", label: "Call Success" },
                                    interested:  { bg: "rgba(59,130,246,.14)",  color: "#1d4ed8", label: "Meeting" },
                                    callBack:    { bg: "rgba(245,158,11,.14)",  color: "#92400e", label: "Call Back" },
                                    noAnswer:    { bg: "rgba(107,114,128,.12)", color: "#374151", label: "No Answer" },
                                  };
                                  const meta = dispMeta[d] || { bg: "rgba(107,114,128,.12)", color: "#374151", label: d };
                                  return (
                                    <span
                                      key={d}
                                      style={{
                                        background: meta.bg,
                                        color: meta.color,
                                        borderRadius: 6,
                                        padding: "2px 8px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {meta.label}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            )}
                          </td>
                          <td>
                            <span className="fw-bold" style={{ color: "#111827" }}>
                              {c.calls_completed || 0}
                            </span>
                          </td>
                          <td style={{ color: "#6b7280", fontSize: 13 }}>
                            <div>{new Date(c.created_at).toLocaleDateString()}</div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>{new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                          </td>
                          <td style={{ fontSize: 13 }}>
                            {c.assignees && c.assignees.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {c.assignees.map((id) => {
                                  const ag = agents.find((a) => a.value === String(id));
                                  return ag ? (
                                    <span
                                      key={id}
                                      style={{
                                        background: "rgba(99,102,241,.1)",
                                        color: "#4f46e5",
                                        borderRadius: 6,
                                        padding: "2px 8px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {ag.label}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {isAdmin && (
                                <div className="form-check form-switch mb-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    style={{ width: 36, height: 20, cursor: "pointer" }}
                                    checked={c.status === "active"}
                                    onChange={() => handleToggleCampaignStatus(c)}
                                  />
                                </div>
                              )}
                              <InlineBadge bg={sm.bg} color={sm.color} label={sm.label} />
                            </div>
                          </td>
                          <td className="pe-4">
                            <div className="d-flex align-items-center gap-2">
                              {!powerDialer.session &&
                                c.status !== "stopped" &&
                                c.status === "active" && (
                                  <button
                                    className="btn btn-sm fw-semibold d-flex align-items-center gap-1"
                                    style={{
                                      background: "rgba(16,185,129,.12)",
                                      color: "#065f46",
                                      borderRadius: 8,
                                      border: "none",
                                      padding: "5px 12px",
                                      fontSize: 13,
                                    }}
                                    title="Start Session"
                                    onClick={() => handleStartSession(c._id)}
                                  >
                                    <i className="ti ti-player-play" style={{ fontSize: 14 }} />
                                    Start
                                  </button>
                                )}
                              <div style={{ position: "relative" }}>
                                <button
                                  className="btn btn-sm d-flex align-items-center justify-content-center"
                                  style={{
                                    background: "rgba(107,114,128,.1)",
                                    color: "#374151",
                                    borderRadius: 8,
                                    border: "none",
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openDropdownId === c._id) { setOpenDropdownId(null); return; }
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDropdownPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX - 170 });
                                    setOpenDropdownId(c._id);
                                  }}
                                >
                                  <i className="ti ti-dots-vertical" style={{ fontSize: 16 }} />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {campaigns.length > CAMPAIGN_PAGE_SIZE && (
                <div className="d-flex align-items-center justify-content-between px-4 py-3" style={{ borderTop: "1px solid #f0f1f3" }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>
                    Showing {((campaignPage - 1) * CAMPAIGN_PAGE_SIZE) + 1}–{Math.min(campaignPage * CAMPAIGN_PAGE_SIZE, campaigns.length)} of {campaigns.length}
                  </span>
                  <div className="d-flex gap-1">
                    <button
                      className="btn btn-sm"
                      style={{ borderRadius: 6, border: "1px solid #e5e7eb", color: "#374151", padding: "4px 10px", fontSize: 13 }}
                      disabled={campaignPage === 1}
                      onClick={() => setCampaignPage(p => p - 1)}
                    >‹ Prev</button>
                    {Array.from({ length: Math.ceil(campaigns.length / CAMPAIGN_PAGE_SIZE) }, (_, i) => i + 1).map(pg => (
                      <button
                        key={pg}
                        className="btn btn-sm"
                        style={{ borderRadius: 6, border: campaignPage === pg ? "none" : "1px solid #e5e7eb", background: campaignPage === pg ? "#4f46e5" : "white", color: campaignPage === pg ? "white" : "#374151", padding: "4px 10px", fontSize: 13, fontWeight: campaignPage === pg ? 700 : 400 }}
                        onClick={() => setCampaignPage(pg)}
                      >{pg}</button>
                    ))}
                    <button
                      className="btn btn-sm"
                      style={{ borderRadius: 6, border: "1px solid #e5e7eb", color: "#374151", padding: "4px 10px", fontSize: 13 }}
                      disabled={campaignPage === Math.ceil(campaigns.length / CAMPAIGN_PAGE_SIZE)}
                      onClick={() => setCampaignPage(p => p + 1)}
                    >Next ›</button>
                  </div>
                </div>
              )}

              {/* Fixed-position custom dropdown (escapes all overflow containers) */}
              {openDropdownId && (() => {
                const c = campaigns.find(x => x._id === openDropdownId);
                if (!c) return null;
                return (
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: "fixed",
                      top: dropdownPos.top,
                      left: dropdownPos.left,
                      zIndex: 9999,
                      background: "white",
                      borderRadius: 10,
                      border: "1px solid #f0f1f3",
                      boxShadow: "0 4px 20px rgba(0,0,0,.12)",
                      minWidth: 170,
                      padding: "6px 0",
                      overflow: "hidden",
                    }}
                  >
                    {[
                      { label: "View Contacts", icon: "ti ti-eye", color: "#0f766e", action: () => { handleViewContacts(c); setOpenDropdownId(null); } },
                      ...(isAdmin ? [{ label: "Edit Campaign", icon: "ti ti-pencil", color: "#4f46e5", action: () => { handleOpenEdit(c); setOpenDropdownId(null); } }] : []),
                      { label: "Download Excel", icon: "ti ti-file-spreadsheet", color: "#065f46", action: () => { handleDownloadExcel(c); setOpenDropdownId(null); } },
                      { label: "Download PDF", icon: "ti ti-file-type-pdf", color: "#b91c1c", action: () => { handleDownloadPDF(c); setOpenDropdownId(null); } },
                    ].map(item => (
                      <button
                        key={item.label}
                        onClick={item.action}
                        className="d-flex align-items-center gap-2 w-100"
                        style={{ background: "none", border: "none", padding: "8px 16px", fontSize: 13, color: item.color, cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >
                        <i className={item.icon} style={{ fontSize: 15 }} />
                        {item.label}
                      </button>
                    ))}
                    {isAdmin && (
                      <>
                        <div style={{ borderTop: "1px solid #f0f1f3", margin: "4px 0" }} />
                        <button
                          onClick={() => { handleDeleteCampaign(c._id); setOpenDropdownId(null); }}
                          className="d-flex align-items-center gap-2 w-100"
                          style={{ background: "none", border: "none", padding: "8px 16px", fontSize: 13, color: "#dc2626", cursor: "pointer", textAlign: "left" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          <i className="ti ti-trash" style={{ fontSize: 15 }} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
              </>
            )}
          </div>
        )}

        {/* ── Live Calls ── */}
        {activeTab === "live" && isAdmin && (
          <div>
            <div className="row g-3 mb-4">
              {[
                { label: "Total Calls", value: liveData.stats?.total_calls ?? 0, icon: "ti-phone", iconBg: "rgba(99,102,241,.12)", iconColor: "#4f46e5" },
                { label: "Completed", value: liveData.stats?.completed_calls ?? 0, icon: "ti-circle-check", iconBg: "rgba(16,185,129,.12)", iconColor: "#059669" },
                { label: "Pending", value: liveData.stats?.pending_calls ?? 0, icon: "ti-clock", iconBg: "rgba(245,158,11,.12)", iconColor: "#d97706" },
                { label: "Failed", value: liveData.stats?.failed_calls ?? 0, icon: "ti-circle-x", iconBg: "rgba(239,68,68,.12)", iconColor: "#dc2626" },
                { label: "Active Users", value: liveData.stats?.active_users ?? 0, icon: "ti-users", iconBg: "rgba(139,92,246,.12)", iconColor: "#7c3aed" },
              ].map((s) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>

            <div className="card border-0" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
              {(liveData.rows || []).length === 0 ? (
                <div className="py-5 text-center">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                    style={{ width: 56, height: 56, background: "rgba(99,102,241,.1)" }}
                  >
                    <i className="ti ti-phone fs-2 text-primary" />
                  </div>
                  <p className="fw-semibold mb-1" style={{ color: "#111827" }}>
                    No active calls
                  </p>
                  <p className="text-muted small mb-0">Live calls will appear here</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        {["Campaign", "Agent", "From", "To (Contact)", "Started", "Status"].map((h) => (
                          <th
                            key={h}
                            className="fw-semibold text-muted text-uppercase py-3 ps-4"
                            style={{ fontSize: 11, letterSpacing: ".6px", borderBottom: "1px solid #f0f1f3" }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {liveData.rows.map((r) => (
                        <tr key={r.session_id} style={{ borderBottom: "1px solid #f4f5f7" }}>
                          <td className="ps-4 fw-semibold" style={{ fontSize: 14, color: "#111827" }}>
                            {r.campaign_name}
                          </td>
                          <td className="ps-4" style={{ fontSize: 13, color: "#374151" }}>
                            {r.agent_name}
                          </td>
                          <td className="ps-4" style={{ fontSize: 13, color: "#6b7280" }}>
                            {r.from_number}
                          </td>
                          <td className="ps-4" style={{ fontSize: 13 }}>
                            {r.contact_name} {r.to_number}
                          </td>
                          <td className="ps-4" style={{ fontSize: 13, color: "#6b7280" }}>
                            {new Date(r.start_time).toLocaleTimeString()}
                          </td>
                          <td className="ps-4">
                            <InlineBadge
                              bg="rgba(16,185,129,.14)"
                              color="#065f46"
                              label={r.status}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      <ConfirmModal
        show={confirm.show}
        title={confirm.title}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        confirmColor={confirm.confirmColor}
        confirmBg={confirm.confirmBg}
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      />

      {/* ── View Campaign Contacts Modal ── */}
      {viewModal && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)", zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 580 }}>
            <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h6 className="fw-bold mb-0" style={{ color: "#111827", fontSize: 16 }}>
                    <i className="ti ti-eye me-2" style={{ color: "#0f766e" }} />
                    {viewCampaign?.name}
                  </h6>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: 12 }}>
                    {viewContacts.length} contact{viewContacts.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <button className="btn-close" onClick={() => setViewModal(false)} />
              </div>
              <div className="modal-body px-4 pb-4">
                {loadingContacts ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm text-primary" />
                  </div>
                ) : viewContacts.length === 0 ? (
                  <p className="text-muted text-center py-4 mb-0">No contacts in this list.</p>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: "auto", borderRadius: 10, border: "1px solid #f0f1f3" }}>
                    <table className="table align-middle mb-0">
                      <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
                        <tr>
                          {["#", "Name", "Phone", "Status", "Calls"].map((h) => (
                            <th
                              key={h}
                              className="fw-semibold text-muted text-uppercase py-3 ps-3"
                              style={{ fontSize: 11, letterSpacing: ".6px", border: "none", borderBottom: "1px solid #f0f1f3" }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewContacts.map((c, i) => {
                          const statusMap = {
                            pending:   { bg: "rgba(107,114,128,.12)", color: "#374151", label: "Pending" },
                            called:    { bg: "rgba(16,185,129,.14)",  color: "#065f46", label: "Called" },
                            no_answer: { bg: "rgba(245,158,11,.14)",  color: "#92400e", label: "No Answer" },
                            busy:      { bg: "rgba(99,102,241,.14)",  color: "#3730a3", label: "Busy" },
                            failed:    { bg: "rgba(239,68,68,.14)",   color: "#991b1b", label: "Failed" },
                          };
                          const dispositionMap = {
                            callBack:    { bg: "rgba(99,102,241,.14)",  color: "#3730a3", label: "Call Back" },
                            noAnswer:    { bg: "rgba(245,158,11,.14)",  color: "#92400e", label: "No Answer" },
                            interested:  { bg: "rgba(16,185,129,.14)",  color: "#065f46", label: "Meeting" },
                            callSuccess: { bg: "rgba(20,184,166,.14)",  color: "#0f766e", label: "Call Success" },
                          };
                          // Use per-campaign disposition so contacts from other campaigns don't show wrong status
                          const campaignDisp = c.campaign_disposition;
                          const badge = campaignDisp && dispositionMap[campaignDisp]
                            ? dispositionMap[campaignDisp]
                            : campaignDisp === ""
                              ? statusMap.called
                              : statusMap.pending;
                          return (
                            <tr key={c._id || i} style={{ borderBottom: "1px solid #f9fafb" }}>
                              <td className="ps-3 text-muted" style={{ fontSize: 13 }}>{i + 1}</td>
                              <td className="ps-3" style={{ fontSize: 14, fontWeight: 500 }}>{c.name || "—"}</td>
                              <td className="ps-3" style={{ fontSize: 14, color: "#374151" }}>{c.phone}</td>
                              <td className="ps-3">
                                <span
                                  className="fw-semibold"
                                  style={{ background: badge.bg, color: badge.color, borderRadius: 6, padding: "3px 10px", fontSize: 12 }}
                                >
                                  {badge.label}
                                </span>
                              </td>
                              <td className="ps-3" style={{ fontSize: 13, color: "#6b7280" }}>
                                {c.campaign_attempt_count > 0 ? `${c.campaign_attempt_count}×` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Campaign Modal ── */}
      {editModal && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)", zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
            <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <h6 className="fw-bold mb-0" style={{ color: "#111827", fontSize: 16 }}>
                  <i className="ti ti-pencil me-2" style={{ color: "#4f46e5" }} />
                  Edit Campaign
                </h6>
                <button className="btn-close" onClick={() => setEditModal(false)} />
              </div>
              <div className="modal-body px-4 pb-2 d-flex flex-column gap-3">
                <FormField label="Campaign Name" required>
                  <input
                    className="form-control"
                    style={{ borderRadius: 10 }}
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </FormField>
                <FormField label="Description">
                  <textarea
                    className="form-control"
                    rows={2}
                    style={{ borderRadius: 10, resize: "none" }}
                    value={editForm.description}
                    onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </FormField>
              </div>
              <div className="modal-footer border-0 px-4 pt-2 pb-4 gap-2">
                <button
                  className="btn fw-semibold px-4"
                  style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
                  onClick={() => setEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn fw-semibold px-4"
                  style={{ background: "#4f46e5", color: "#fff", borderRadius: 10, border: "none" }}
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
